import React from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, alpha, } from '@mui/material/styles';
import { APP_BAR_HEIGHT, COLORS, FONT_WEIGHTS } from './constants';
let theme = createTheme({
    breakpoints: {
        values: {
            xs: 0,
            sm: 480,
            md: 768,
            lg: 1024,
            xl: 1200,
        },
    },
    palette: {
        primary: { main: COLORS.PRIMARY },
        secondary: { main: COLORS.SECONDARY },
        black: COLORS.BLACK,
        gray: COLORS.GRAY,
        lightGray: COLORS.LIGHT_GRAY,
        white: COLORS.WHITE,
        orange: COLORS.ORANGE,
        text: {
            disabled: COLORS.GRAY,
        },
        transparent: COLORS.TRANSPARENT,
    },
    typography: {
        allVariants: {
            fontFamily: ['Nunito Sans', 'sans-serif'].join(','),
        },
    },
});
const { breakpoints } = theme;
const mdBreakpoint = `@media (max-width:${breakpoints.values.md}px)`;
// Refererence: Theme composition in https://mui.com/material-ui/customization/theming/#api
theme = createTheme(theme, {
    typography: {
        h1: {
            fontSize: 48,
            color: COLORS.SECONDARY,
            [mdBreakpoint]: {
                fontSize: 36,
            },
        },
        h2: {
            fontSize: 36,
            color: COLORS.SECONDARY,
            [mdBreakpoint]: {
                fontSize: 30,
            },
        },
        h3: {
            fontSize: 26,
            color: COLORS.BLACK,
            [mdBreakpoint]: {
                fontSize: 24,
            },
        },
        h4: {
            fontSize: 20,
            color: COLORS.BLACK,
        },
        h5: {
            fontSize: 18,
            color: COLORS.BLACK,
        },
        subtitle1: {
            fontSize: 16,
            color: COLORS.BLACK,
        },
        body1: {
            fontSize: 14,
            color: COLORS.BLACK,
        },
        body2: {
            fontSize: 12,
            color: COLORS.BLACK,
            lineHeight: 1.5,
        },
        fontWeightLight: FONT_WEIGHTS.LIGHT,
        fontWeightRegular: FONT_WEIGHTS.REGULAR,
        fontWeightBold: FONT_WEIGHTS.BOLD,
    },
    components: {
        MuiAccordion: {
            styleOverrides: {
                root: {
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: COLORS.LIGHT_GRAY,
                },
            },
        },
        MuiAccordionDetails: {
            styleOverrides: {
                root: {
                    padding: 8,
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: COLORS.LIGHT_GRAY,
                },
            },
        },
        MuiAccordionSummary: {
            styleOverrides: {
                root: {
                    minHeight: 'unset',
                    backgroundColor: COLORS.WHITE,
                },
                content: {
                    margin: '16px 0',
                },
            },
        },
        MuiDivider: {
            variants: [
                {
                    props: { variant: 'Primary' },
                    style: {
                        borderColor: COLORS.PRIMARY,
                    },
                },
                {
                    props: { variant: 'Secondary' },
                    style: {
                        borderColor: COLORS.SECONDARY,
                    },
                },
                {
                    props: { variant: 'Tertiary' },
                    style: {
                        borderColor: COLORS.LIGHT_GRAY,
                    },
                },
            ],
        },
        MuiButton: {
            defaultProps: {
                disableRipple: true,
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    fontSize: 20,
                    fontWeight: FONT_WEIGHTS.BOLD,
                    textTransform: 'capitalize',
                    lineHeight: '27px',
                    padding: '14px 32px',
                    color: COLORS.WHITE,
                    '&:disabled': {
                        background: COLORS.GRAY,
                        color: COLORS.WHITE,
                    },
                },
                // Primary
                containedPrimary: {
                    '&:hover, &:focus': {
                        borderWidth: 1.5,
                        borderColor: COLORS.SECONDARY,
                        backgroundColor: COLORS.SECONDARY,
                    },
                },
                // Secondary
                outlinedPrimary: {
                    color: COLORS.PRIMARY,
                    borderWidth: 1.5,
                    '&:hover, &:focus': {
                        borderWidth: 1.5,
                        borderColor: COLORS.SECONDARY,
                        backgroundColor: COLORS.TRANSPARENT,
                        color: COLORS.SECONDARY,
                    },
                    '&:disabled': {
                        borderColor: COLORS.GRAY,
                        background: COLORS.TRANSPARENT,
                        color: COLORS.GRAY,
                    },
                },
                // Tertiary
                textPrimary: {
                    borderWidth: 2,
                    borderStyle: 'solid',
                    borderColor: COLORS.TRANSPARENT,
                    borderRadius: 0,
                    color: COLORS.SECONDARY,
                    background: COLORS.TRANSPARENT,
                    padding: '9px 0px',
                    '&:hover, &:focus': {
                        borderBottomWidth: 2,
                        borderBottomStyle: 'solid',
                        borderBottomColor: COLORS.PRIMARY,
                        borderRadius: 0,
                        backgroundColor: COLORS.TRANSPARENT,
                    },
                    '&:disabled': {
                        background: COLORS.TRANSPARENT,
                        color: COLORS.GRAY,
                    },
                },
            },
        },
        MuiAlert: {
            variants: [
                {
                    props: { severity: 'success' },
                    style: {
                        borderRadius: 10,
                    },
                },
                {
                    props: { severity: 'error' },
                    style: {
                        borderRadius: 10,
                    },
                },
                {
                    props: { severity: 'warning' },
                    style: {
                        borderRadius: 10,
                    },
                },
                {
                    props: { severity: 'info' },
                    style: {
                        borderRadius: 10,
                        backgroundColor: COLORS.INFOBLUE,
                    },
                },
            ],
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 4,
                },
            },
        },
        MuiRadio: {
            defaultProps: {
                disableRipple: true,
            },
            styleOverrides: {
                colorPrimary: {
                    '&:hover': {
                        color: COLORS.PRIMARY,
                        backgroundColor: COLORS.TRANSPARENT,
                    },
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    borderBottomColor: COLORS.LIGHT_GRAY,
                    borderLeft: 'none',
                    borderRight: 'none',
                    borderTop: 'none',
                    background: COLORS.WHITE,
                    minHeight: APP_BAR_HEIGHT,
                },
            },
        },
        MuiToolbar: {
            styleOverrides: {
                regular: {
                    height: APP_BAR_HEIGHT,
                    minHeight: APP_BAR_HEIGHT,
                },
            },
        },
        MuiInputBase: {
            defaultProps: {
                color: 'secondary',
            },
            styleOverrides: {
                root: {
                    backgroundColor: COLORS.WHITE,
                    '.MuiInputBase-input.Mui-disabled': {
                        backgroundColor: '#F2f2f2',
                        borderColor: COLORS.GRAY,
                        WebkitTextFillColor: COLORS.GRAY,
                    },
                },
                formControl: {
                    '&:hover': {
                        boxShadow: `0 0 5px ${COLORS.GRAY}`,
                    },
                },
            },
        },
        MuiPaper: {
            defaultProps: {
                variant: 'elevation',
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    whiteSpace: 'break-spaces',
                    '.MuiSvgIcon-root': {
                        fontSize: 16,
                    },
                },
            },
        },
        MuiStepIcon: {
            variants: [
                {
                    props: { active: true },
                    style: {
                        fill: COLORS.WHITE,
                        stroke: COLORS.SECONDARY,
                        width: '1.2em',
                        height: '1.2em',
                        overflow: 'visible',
                    },
                },
                {
                    props: { completed: true },
                    style: {
                        fill: COLORS.PRIMARY,
                    },
                },
                {
                    props: { completed: false, active: false },
                    style: {
                        fill: COLORS.SECONDARY,
                    },
                },
            ],
            styleOverrides: {
                text: {
                    display: 'none',
                },
            },
        },
        MuiStepper: {
            styleOverrides: {
                root: {
                    '.MuiStepConnector-root.Mui-active .MuiStepConnector-line': {
                        borderColor: COLORS.PRIMARY,
                    },
                    '.MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
                        borderColor: COLORS.PRIMARY,
                    },
                },
            },
        },
        MuiStepLabel: {
            styleOverrides: {
                root: {
                    '.MuiStepLabel-label.Mui-active': {
                        fontWeight: FONT_WEIGHTS.BOLD,
                        color: COLORS.SECONDARY,
                    },
                    '.MuiSvgIcon-root.Mui-pending': {
                        fill: COLORS.ORANGE,
                        width: '1.2em',
                        height: '1.2em',
                    },
                },
            },
        },
        MuiStepButton: {
            styleOverrides: {
                root: {
                    '.MuiSvgIcon-root.Mui-pending': {
                        fill: COLORS.ORANGE,
                        width: '1.2em',
                        height: '1.2em',
                    },
                },
            },
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    cursor: 'pointer',
                },
            },
        },
        MuiTable: {
            styleOverrides: {
                root: {
                    borderCollapse: 'collapse',
                },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    border: `1px solid ${COLORS.GRAY}`,
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderBottom: 'none',
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    ':nth-of-type(even)': {
                        backgroundColor: alpha(COLORS.GRAY, 0.1),
                    },
                },
                hover: {
                    backgroundColor: alpha(COLORS.LIGHT_GRAY, 0.4),
                },
            },
        },
        MuiToggleButton: {
            defaultProps: {
                disableRipple: true,
            },
            styleOverrides: {
                root: {
                    textTransform: 'capitalize',
                    fontSize: 16,
                    color: COLORS.BLACK,
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                icon: {
                    fill: COLORS.PRIMARY,
                },
                root: {
                    '.MuiSelect-select': {
                        backgroundColor: COLORS.WHITE,
                    },
                },
            },
        },
    },
});
export const ThemeProvider = ({ children }) => {
    return React.createElement(MuiThemeProvider, { theme: theme }, children);
};
export { theme };
export * from './constants';
